import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'

import VideoModal from '../ui/VideoModal'
import { VideoLibrary, Notice } from './media'
import './media.css'

/*
 * SPONSOR — the video picker.
 *
 * Requests untouched:
 *   GET  {VIDEOSERVER_API}upload/{APP_NAME}          — the library
 *   POST admin/sponsors/challengevideos              { addVideo, ChallengeID, VideoID }
 *
 * The body is the shared VideoLibrary, so the sponsor picker and the coaches
 * picker are the same screen. Two things behave better than they did:
 *   - the search reads the full library every keystroke instead of narrowing an
 *     already-narrowed list (typing then deleting used to leave rows missing),
 *     and a clip with no friendlyName can now be found by id;
 *   - the request can fail visibly instead of leaving the dialog on "Loading".
 */

export default function SponsorSelectVideo(props) {

  const [videos, setVideos] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [busyId, setBusyId] = useState('')
  const [preview, setPreview] = useState(-1)

  useEffect(() => {
    loadVideos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadVideos = () => {
    setIsLoading(true)
    setError('')
    axios.get(process.env.REACT_APP_VIDEOSERVER_API + 'upload/' + process.env.REACT_APP_NAME)
      .then(res => {
        setVideos(res.data.sort((a, b) => (a.Timestamp < b.Timestamp) ? 1 : -1))
        setIsLoading(false)
      })
      .catch(err => {
        setIsLoading(false)
        setError('The video library could not be loaded.')
      })
  }

  const shown = useMemo(() => {
    const all = Array.isArray(videos) ? videos.filter((item) => item && item.id) : []
    const s = query.trim().toUpperCase()
    if (!s) return all
    return all.filter((item) => {
      const id = String(item.id || '').toUpperCase()
      const friendly = String(item.friendlyName || '').toUpperCase()
      return id.indexOf(s) > -1 || friendly.indexOf(s) > -1
    })
  }, [videos, query])

  const clips = useMemo(
    () => shown.map((item) => ({
      fileId: item.id,
      name: item.friendlyName ? item.friendlyName : item.id,
      subtitle: item.id,
    })),
    [shown]
  )

  useEffect(() => {
    if (preview >= clips.length) setPreview(-1)
  }, [clips.length, preview])

  const selectVideo = (item, next) => {

    const obj = {
      addVideo: next,
      ChallengeID: props.ChallengeID,
      VideoID: item.id
    }
    setBusyId(item.id)
    setError('')
    axios.post(process.env.REACT_APP_API_SERVER + 'admin/sponsors/challengevideos', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusyId('')
        if (res.data.err == 0) {
          props.loadData()
          props.handleClose()
        } else {
          setError('Something went wrong, please try again')
        }
      }).catch(err => {
        setBusyId('')
        setError('Something went wrong, please try again')
      })
  }

  return (
    <>
      {error ? <Notice kind="crit" onClose={() => setError('')}>{error}</Notice> : null}

      <VideoLibrary
        videos={shown}
        loading={isLoading}
        query={query}
        onQuery={setQuery}
        isPicked={() => false}
        onPick={(item) => selectVideo(item, true)}
        onPreview={(item) => setPreview(shown.indexOf(item))}
        busyId={busyId}
        pickLabel="Add"
        emptyTitle="No videos"
        emptyHint="No videos match these filters."
      />

      {preview >= 0 && clips[preview] ? (
        <VideoModal
          items={clips}
          index={preview}
          title="Video library"
          onIndex={setPreview}
          onClose={() => setPreview(-1)}
        />
      ) : null}
    </>
  )
}
